<template>
	<div class="phone-step" :class="`phone-step--${variant}`">
		<h3 class="step-title">{{ copy.stepTitle }}</h3>
		<p class="step-desc">{{ copy.stepDesc }}</p>

		<div class="phone-field" :class="{ filled: phoneNumber }">
			<n-popover trigger="click" placement="bottom-start" raw>
				<template #trigger>
					<button class="area-trigger" type="button" @click="handleAreaTriggerClick">
						<span>{{ areaCode.text || "+86" }}</span>
						<img :src="pointIcon" alt="" />
					</button>
				</template>
				<div class="area-list">
					<button
						v-for="item in areaOptions"
						:key="`${item.code}-${item.text}`"
						class="area-item"
						type="button"
						@click="selectArea(item)"
					>
						<span>{{ item.code.replace("00", "+") }}</span>
						<span>{{ item.text }}</span>
					</button>
				</div>
			</n-popover>
			<input
				v-model="phoneNumber"
				type="tel"
				class="phone-input"
				:placeholder="copy.placeholder"
				@input="handleInput"
				@focus="handlePhoneFocus"
			/>
			<button v-if="phoneNumber" class="clear-btn" type="button" @click="clearPhone">
				<img :src="clearIcon" :alt="copy.clearAlt" />
			</button>
		</div>

		<template v-if="isIntegrated">
			<div class="code-row">
				<input
					v-model="verifyCode"
					type="text"
					class="code-input"
					inputmode="numeric"
					maxlength="6"
					:placeholder="copy.codePlaceholder"
					@input="handleCodeInput"
					@focus="handleCodeFocus"
				/>
				<button
					class="send-code-btn"
					type="button"
					:disabled="countdown > 0"
					@click="handleSendCode"
				>
					<span v-if="countdown > 0">{{ countdown }}s</span>
					<span v-else>{{ copy.sendCode }}</span>
				</button>
			</div>

			<a :href="kefuLink" class="kefu" @click="$emit('kefu-click')">{{ copy.kefuHelp }}</a>
		</template>

		<ClientOnly>
			<div class="turnstile-container">
				<VueTurnstile
					v-if="turnstileSiteKey"
					v-model="turnstileToken"
					:site-key="turnstileSiteKey"
					theme="light"
					:language="turnstileLanguage"
					@error="onTurnstileError"
					@expired="onTurnstileExpired"
					@unsupported="onTurnstileUnsupported"
				/>
			</div>
		</ClientOnly>

		<button v-if="showSwitch" class="switch-link" type="button" @click="$emit('switch', 'email')">
			{{ copy.switchEmail }}
		</button>

		<template v-if="isIntegrated">
			<div class="next-btn-container">
				<button
					class="next-btn"
					:class="{ enabled: canSubmit }"
					type="button"
					@click="handleSubmit"
				>
					{{ copy.next }}
				</button>
			</div>
		</template>
		<button
			v-else
			class="next-btn"
			:class="{ enabled: canProceed }"
			type="button"
			@click="handleNext"
		>
			{{ copy.next }}
		</button>
	</div>
</template>

<script setup>
import { computed, inject, onMounted, reactive, ref, watch } from "vue";
import useLogin from "@/hooks/login/index";
import { countryCode } from "~/utils/countryCode";
import showMessage from "~/utils/showMessage";
import { useI18n } from "~/composables/useI18n";
import { LOGIN_AUTH_METHODS } from "~/composables/useSiteComponentTracking";
import { isSandbox } from "~/utils/common";
import VueTurnstile from "vue-turnstile";
import clearIcon from "./images/clear.png";
import pointIcon from "./images/point.png";

const props = defineProps({
	/** integrated: 一步式（新登录）| step: 多步式（企业中心） */
	variant: {
		type: String,
		default: "integrated",
		validator: v => ["integrated", "step"].includes(v),
	},
	showSwitch: { type: Boolean, default: true },
	presetPhone: { type: String, default: "" },
	presetAreaCode: { type: String, default: "" },
	countdown: { type: Number, default: 0 },
	kefuLink: { type: String, default: "" },
	/** step 变体 UI 語言：`zh-TW`（預設）| `en` */
	locale: { type: String, default: "zh-TW" },
});

const emit = defineEmits(["send-code", "submit", "next", "switch", "kefu-click"]);
const { locale: i18nLocale } = useI18n();
const logLoginEvent = inject("logLoginEvent", null);

const isIntegrated = computed(() => props.variant === "integrated");
const isEn = computed(() =>
	isIntegrated.value ? i18nLocale.value === "en" : props.locale === "en"
);

const integratedCopy = {
	en: {
		stepTitle: "Enter your mobile number",
		stepDesc: "We protect your privacy. Others will not see this information.",
		placeholder: "Enter your mobile number",
		codePlaceholder: "Enter verification code",
		sendCode: "Send code",
		kefuHelp: "Didn't receive the code?",
		clearAlt: "clear",
		switchEmail: "Sign up / sign in with email",
		next: "Login / Register",
		selectAreaCode: "Please select a country code",
		captchaRequired: "Please complete the verification challenge",
		codeRequired: "Please enter the verification code",
		codeInvalid: "Please enter a 6-digit verification code",
		turnstileError: "Verification failed. Please try again.",
		turnstileExpired: "Verification expired. Please verify again.",
		turnstileUnsupported: "This browser is not supported. Please try another browser.",
	},
	zh: {
		stepTitle: "輸入您的手機號碼",
		stepDesc: "我們會保障您的隱私，其他人不會看到這項資料。",
		placeholder: "輸入您的手機號碼",
		codePlaceholder: "請輸入驗證碼",
		sendCode: "發送驗證碼",
		kefuHelp: "無法收到驗證碼？",
		clearAlt: "clear",
		switchEmail: "使用電郵地址註冊/登錄",
		next: "登入 / 註冊",
		selectAreaCode: "請選擇區號",
		captchaRequired: "請先進行人機校驗",
		codeRequired: "請輸入驗證碼",
		codeInvalid: "請輸入6位數字驗證碼",
		turnstileError: "驗證失敗，請重試",
		turnstileExpired: "驗證已過期，請重新驗證",
		turnstileUnsupported: "當前瀏覽器不支持驗證，請使用其他瀏覽器",
	},
};

const stepCopy = {
	en: {
		stepTitle: "Enter your mobile number",
		stepDesc: "We protect your privacy. Others will not see this information.",
		placeholder: "Enter your mobile number",
		clearAlt: "clear",
		switchEmail: "Sign up / sign in with email",
		next: "Next",
		selectAreaCode: "Please select a country code",
		captchaRequired: "Please complete the verification challenge",
		turnstileError: "Verification failed. Please try again.",
		turnstileExpired: "Verification expired. Please verify again.",
		turnstileUnsupported: "This browser is not supported. Please try another browser.",
	},
	zh: {
		stepTitle: "輸入您的手機號碼",
		stepDesc: "我們會保障您的隱私，其他人不會看到這項資料。",
		placeholder: "輸入您的手機號碼",
		clearAlt: "clear",
		switchEmail: "使用電郵地址註冊/登錄",
		next: "下一步",
		selectAreaCode: "請選擇區號",
		captchaRequired: "請先進行人機校驗",
		turnstileError: "驗證失敗，請重試",
		turnstileExpired: "驗證已過期，請重新驗證",
		turnstileUnsupported: "當前瀏覽器不支持驗證，請使用其他瀏覽器",
	},
};

const copy = computed(() => {
	const source = isIntegrated.value ? integratedCopy : stepCopy;
	return isEn.value ? source.en : source.zh;
});

const turnstileLanguage = computed(() => (isEn.value ? "en" : "zh-TW"));

const { getAreaByIpCode, validPhone } = useLogin();
const areaOptions = computed(() => (isEn.value ? countryCode.en : countryCode.zh_tw));
const phoneNumber = ref("");
const verifyCode = ref("");
const areaCode = reactive({ code: "0086", text: "+86" });
const turnstileToken = ref("");

const turnstileSiteKey = computed(() => {
	if (!import.meta.client) return "";
	try {
		const nuxtApp = useNuxtApp();
		if (nuxtApp.$config?.public?.turnstileSiteKey) {
			return nuxtApp.$config.public.turnstileSiteKey;
		}
		return isSandbox ? "0x4AAAAAACJhx8CBwSokixH0" : "0x4AAAAAACL-kwXc22Bg7eLS";
	} catch (error) {
		console.warn("无法获取 Turnstile 配置:", error);
		return "0x4AAAAAACLMLNc0UFE6nQpt";
	}
});

const canSubmit = computed(
	() => phoneNumber.value.trim().length > 0 && verifyCode.value.length === 6
);
const canProceed = computed(() => phoneNumber.value.trim().length > 0);

const selectArea = item => {
	areaCode.code = item.code;
	areaCode.text = item.code.replace("00", "+");
};

const handleAreaTriggerClick = () => {
	logLoginEvent?.("login_country_code_click", {
		country_code: areaCode.code,
		auth_method: LOGIN_AUTH_METHODS.PHONE,
	});
};

const handlePhoneFocus = () => {
	logLoginEvent?.("login_phone_input_focus", {
		country_code: areaCode.code,
	});
};

const handleCodeFocus = () => {
	logLoginEvent?.("verification_code_input_focus", {
		auth_method: LOGIN_AUTH_METHODS.PHONE,
	});
};

const getFilledFieldCount = () => {
	let count = 0;
	if (phoneNumber.value.trim()) count += 1;
	if (verifyCode.value.trim()) count += 1;
	return count;
};

const getCountryCode = () => areaCode.code;

const applyPresetPhoneData = () => {
	if (props.presetPhone) phoneNumber.value = props.presetPhone;
	if (props.presetAreaCode) {
		const matchedArea = areaOptions.value.find(item => item.code === props.presetAreaCode);
		if (matchedArea) {
			selectArea(matchedArea);
		} else {
			areaCode.code = props.presetAreaCode;
			areaCode.text = props.presetAreaCode.replace("00", "+");
		}
	}
};

const handleInput = () => {
	phoneNumber.value = phoneNumber.value.replace(/\D/g, "");
};

const handleCodeInput = () => {
	verifyCode.value = verifyCode.value.replace(/\D/g, "");
};

const clearPhone = () => {
	phoneNumber.value = "";
};

const validatePhone = async () => {
	if (!areaCode.code) {
		await showMessage(copy.value.selectAreaCode, "warning");
		return false;
	}
	if (!validPhone(phoneNumber.value)) return false;
	if (turnstileSiteKey.value && !turnstileToken.value) {
		await showMessage(copy.value.captchaRequired, "warning");
		return false;
	}
	return true;
};

const handleSendCode = async () => {
	if (props.countdown > 0) return;
	if (!(await validatePhone())) return;
	emit("send-code", {
		phone: phoneNumber.value,
		areaCode: areaCode.code,
		behavioralCaptcha: turnstileToken.value || null,
	});
};

const handleSubmit = async () => {
	if (!(await validatePhone())) return;
	if (!verifyCode.value) {
		await showMessage(copy.value.codeRequired, "warning");
		return;
	}
	if (!/^\d{6}$/.test(verifyCode.value)) {
		await showMessage(copy.value.codeInvalid, "warning");
		return;
	}
	emit("submit", {
		phone: phoneNumber.value,
		areaCode: areaCode.code,
		code: verifyCode.value,
		behavioralCaptcha: turnstileToken.value || null,
	});
};

const handleNext = async () => {
	if (!(await validatePhone())) return;
	emit("next", {
		phone: phoneNumber.value,
		areaCode: areaCode.code,
		behavioralCaptcha: turnstileToken.value || null,
	});
};

const onTurnstileError = async error => {
	console.error("Turnstile 验证错误:", error);
	turnstileToken.value = "";
	await showMessage(copy.value.turnstileError, "error");
};

const onTurnstileExpired = async () => {
	turnstileToken.value = "";
	await showMessage(copy.value.turnstileExpired, "warning");
};

const onTurnstileUnsupported = async () => {
	turnstileToken.value = "";
	await showMessage(copy.value.turnstileUnsupported, "error");
};

onMounted(async () => {
	if (!props.presetAreaCode) {
		const ipCode = await getAreaByIpCode(areaOptions.value);
		if (ipCode) selectArea(ipCode);
	}
	applyPresetPhoneData();
});

watch(() => [props.presetPhone, props.presetAreaCode], applyPresetPhoneData, { immediate: true });

defineExpose({
	getFilledFieldCount,
	getCountryCode,
});
</script>

<style lang="scss" scoped>
.phone-step {
	.step-title {
		margin: 0 0 12px;
		font-size: 24px;
		font-weight: 700;
		line-height: 1.35;
		color: #333333;
	}
	.step-desc {
		margin: 0 0 24px;
		font-size: 16px;
		line-height: 1.5;
		color: #7f7f7f;
	}
	.area-trigger {
		display: flex;
		align-items: center;
		flex-shrink: 0;
		gap: 6px;
		height: 100%;
		min-width: 88px;
		padding: 0 14px 0 12px;
		background: transparent;
		border: 0;
		font-size: 16px;
		color: #333333;
		cursor: pointer;
		img {
			width: 12px;
			height: 12px;
		}
	}
	.phone-input {
		flex: 1;
		height: 100%;
		border: 0;
		outline: none;
		color: #333333;
		background: transparent;
		&::placeholder {
			color: #b7b7b7;
		}
	}
	.clear-btn {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 18px;
		height: 18px;
		padding: 0;
		background: transparent;
		border: 0;
		cursor: pointer;
		img {
			width: 18px;
			height: 18px;
		}
	}
	.switch-link {
		margin-top: 14px;
		padding: 0;
		background: transparent;
		border: 0;
		font-size: 15px;
		color: #4a7dff;
		cursor: pointer;
	}
	.turnstile-container {
		display: flex;
		justify-content: center;
		align-items: center;
	}

	&--integrated {
		.phone-field {
			display: flex;
			align-items: center;
			width: 100%;
			height: 60px !important;
			padding: 0 12px 0 0;
			background: #ffffff;
			border: 1px solid #e5e5e5;
			border-radius: 8px;
			box-sizing: border-box;
		}
		.area-trigger {
			font-weight: bold;
		}
		.phone-input {
			font-size: 18px !important;
		}
		.code-row {
			display: flex;
			align-items: center;
			width: 100%;
			gap: 12px;
			margin-top: 16px;
			box-sizing: border-box;
		}
		.kefu {
			display: block;
			font-size: 16px;
			color: #999999;
			margin: 20px 0 0;
			text-decoration: none;
			cursor: pointer;
			&:hover {
				color: #777777;
			}
		}
		.code-input {
			flex: 1;
			min-width: 0;
			height: 60px;
			padding: 0 16px;
			border: 1px solid #e5e5e5;
			border-radius: 8px;
			outline: none;
			font-size: 18px !important;
			color: #333333;
			box-sizing: border-box;
			&::placeholder {
				color: #b7b7b7;
			}
		}
		.send-code-btn {
			flex: 0 0 132px;
			width: 132px;
			height: 60px;
			padding: 0 8px;
			background: #ffffff;
			border: 1px solid #f2af19;
			border-radius: 8px;
			font-size: 20px;
			color: #f2af19;
			cursor: pointer;
			white-space: nowrap;
			box-sizing: border-box;
			&:disabled {
				cursor: not-allowed;
				opacity: 0.7;
			}
		}
		.next-btn-container {
			display: flex;
			justify-content: center;
		}
		.next-btn {
			width: 60%;
			height: 60px;
			margin: 4px auto 0;
			border: 0;
			border-radius: 10px;
			background: #e5e5e5;
			font-size: 20px;
			font-weight: 500;
			color: #ffffff;
			cursor: not-allowed;
			&.enabled {
				background: #111111;
				cursor: pointer;
			}
		}
		.turnstile-container {
			margin-top: 16px;
		}
	}

	&--step {
		.phone-field {
			display: flex;
			align-items: center;
			height: 46px;
			padding: 0 12px 0 0;
			background: #ffffff;
			border: 1px solid #3b3b3b;
			border-radius: 10px;
			transition: border-color 0.2s ease;
			&.filled {
				border-color: #d8d8d8;
			}
		}
		.area-trigger {
			line-height: 1;
			white-space: nowrap;
			span:first-child {
				white-space: nowrap;
			}
			img {
				flex-shrink: 0;
			}
		}
		.phone-input {
			font-size: 16px;
		}
		.switch-link {
			line-height: 1.4;
		}
		.next-btn {
			width: 100%;
			height: 46px;
			margin-top: 22px;
			border: 0;
			border-radius: 10px;
			background: #d9d9d9;
			font-size: 18px;
			font-weight: 600;
			color: #ffffff;
			cursor: not-allowed;
			transition: background-color 0.2s ease;
			&.enabled {
				background: #111111;
				cursor: pointer;
			}
		}
		.turnstile-container {
			margin-top: 14px;
		}
	}
}

:deep(.n-popover) {
	padding: 0;
	border-radius: 12px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}
.area-list {
	max-height: 240px;
	padding: 8px;
	overflow-y: auto;
	background: #ffffff;
	border-radius: 12px;
}
.area-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	width: 100%;
	padding: 10px 12px;
	background: transparent;
	border: 0;
	border-radius: 8px;
	font-size: 14px;
	color: #333333;
	cursor: pointer;
	&:hover {
		background: #f5f5f5;
	}
}
</style>
